import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Enclosure } from "types/Enclosure";
import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import { TbFence } from "react-icons/tb";
import SectorSelect from "components/select/SectorSelect/SectorSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import EnclosureItem from "./EnclosureItem";

function Enclosures() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageEnclosures =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.enclosure?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Enclosure>("enclosures", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "Enclosures_all",
  });
  const { removeItem } = useItem<Enclosure>("enclosures", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/enclosures/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
      {
        Header: t("forms.sector"),
        filterName: "sector",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SectorSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.specy"),
        filterName: "species",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SpecySelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.tag"),
        filterName: "tags",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.enclosures")}
        subtitle={t("titleLegends.enclosures")}
        btnTitle={t("newItem.enclosure")}
        btnSubtitle={t("newItem.enclosureLegend")}
        Icon={TbFence}
        addLink="/enclosures/add"
        addLinkDisabled={!canManageEnclosures}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={EnclosureItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Enclosures;
